import {
  CustomFieldProps,
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  FileFieldProps,
  FileRepresentation,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { Currency, FundingDto, ResponseDoc, SlotDto } from '@/types';
import { combineLatest, map, Observable, of } from 'rxjs';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { PaaSlotsFormComponent } from '../slots-form/slots-form.component';
import { PaaMilestoneRepository } from '@/store/paa-state/milestones-state/milestones.repository';
import { PaaFundingSourceFormComponent } from '../funding-source-form/funding-source-form.component';
import { getFieldBuilderObjectFactory } from '@/components/form-builder/field-builder/utils/field-builder-factory';
import { BudgetEstimateComponent } from '@/components/shared/budget-estimate/budget-estimate.component';
import { addMonths } from 'date-fns';

interface getPaaMilestoneFormObjectProps {
  store: PaaMilestoneRepository;
  download?: (file: FileRepresentation) => void;
  openFile?: (file: FileRepresentation) => void;
  marketTypesOptions?: SelectOption[];
  financialSourcesOptions?: SelectOption[];
  currencyOptions?: SelectOption[];
  selectionModeOptions?: SelectOption[];
  budgetAllocationOptions?: SelectOption[];
  isDisabled?: Observable<boolean>;
  minDate?: Date;
  currencies?: Currency[];
}

export const getPaaMilestoneFormObject = ({
  store,
  download,
  openFile,
  marketTypesOptions,
  financialSourcesOptions,
  currencyOptions,
  selectionModeOptions,
  budgetAllocationOptions,
  isDisabled,
  currencies,
}: getPaaMilestoneFormObjectProps): DynamicForm => {
  const beneficiaryField: DynamicField<TextFieldProps> = {
    label: 'milestone.form.beneficiary',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled,
    error: store.getNestedObservableError('beneficiaire'),
    props: {
      placeholder: 'milestone.form.placeholders.beneficiary',
      value: store.getNestedObservable('milestoneDto', 'beneficiaire'),
      onChange: (value: string) => {
        store.setNested('milestoneDto', 'beneficiaire', value);
        store.clearError('beneficiaire');
      },
    },
  };

  const labelField: DynamicField<TextFieldProps> = {
    label: 'milestone.form.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservableError('realisationEnvisagees'),
    isDisabled,
    props: {
      value: store.getNestedObservable<string>('milestoneDto', 'realisationEnvisagees'),
      onChange: (value: string) => {
        store.setNested('milestoneDto', 'realisationEnvisagees', value);
        store.clearError('realisationEnvisagees');
      },
      placeholder: 'milestone.form.placeholders.label',
    },
  };

  const typeOfMarketField: DynamicField<SelectFieldProps> = {
    label: 'milestone.form.marketType',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservableError('typeMarcher.id', false),
    isDisabled,
    props: {
      value: store.getNestedObservable<SelectOption>('typeOfMarketIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('milestoneDto', 'typeMarcher.id', value.code, false);
        store.setNested('typeOfMarketIdSelected', value);
        store.setNested('milestoneDto', 'modePassation.id', undefined, false);
        store.setNested('selectionModeIdSelected', undefined);
        store.clearError('typeMarcher.id', false);
      },
      placeholder: 'milestone.form.placeholders.market_type',
      options: marketTypesOptions,
    },
  };

  const isSelectionModeDisabled = combineLatest([
    isDisabled instanceof Observable ? isDisabled : of(false),
    store?.getNestedObservable<string>('milestoneDto', 'typeMarcher.id', false) ?? of(null),
  ]).pipe(map(([disabled, typeMarcherId]) => disabled || !typeMarcherId));

  const selectionModeField: DynamicField<SelectFieldProps> = {
    label: 'milestone.form.selection_mode',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: isSelectionModeDisabled,
    error: store.getNestedObservableError('modePassation.id', false),
    props: {
      value: store.getNestedObservable<SelectOption>('selectionModeIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('milestoneDto', 'modePassation.id', value.code, false);
        store.setNested('selectionModeIdSelected', value);
        store.clearError('modePassation.id', false);
      },
      placeholder: 'milestone.form.placeholders.selection_mode',
      options: selectionModeOptions,
    },
  };

  const budgetAllocationField: DynamicField<SelectFieldProps> = {
    label: 'milestone.form.budget_allocation',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled,
    error: store.getNestedObservableError('budgetAllocation.id', false),
    props: {
      value: store.getNestedObservable<SelectOption>('imputationBudgetaireIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('milestoneDto', 'imputationBudgetaire.id', value.code, false);
        store.setNested('imputationBudgetaireIdSelected', value);
        store.clearError('imputationBudgetaire.id', false);
      },
      placeholder: 'milestone.form.placeholders.budget_allocation',
      options: budgetAllocationOptions,
    },
  };

  const budgetEstimatedField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.estimated_budget',
    variant: FieldVariant.CUSTOM,
    props: {
      component: BudgetEstimateComponent,
      value: combineLatest([
        store.getNestedObservable<FundingDto[]>('milestoneDto', 'financements'),
        of(currencies), // or an observable of currencies if dynamic
      ]).pipe(
        map(([list, currencies]) => {
          const result = list.reduce((sum, f) => {
            const currency = currencies?.find(c => c.id === f.monnaieId);
            const amount = f.budgetEstimer || 0;
            const taux = currency?.taux || 1;
            return sum + amount * taux;
          }, 0);
          store.setNested('milestoneDto.budgetEstime', result);
          return result;
        })
      ),
      injectKeys: ['label', 'value'],
    },
  };

  const datePresumedLaunchField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_lps',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservableError('datePrevLps'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevLps'),
      onChange: (value: Date) => {
        store.setNested('milestoneDto', 'datePrevLps', value);
        store.setNested('milestoneDto', 'datePrevAc', addMonths(value, 1));
        store.clearError('datePrevLps');
      },
    },
  };
  const datePresumedAchievementField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_ac',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled: of(true),
    error: store.getNestedObservableError('datePrevAc'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevAc'),
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'milestone.form.files',
    variant: FieldVariant.FILE,
    isDisabled,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('milestoneDto', 'realisationFiles'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('milestoneDto', 'realisationFiles', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'milestoneDto',
          'realisationFiles',
          store.getNested<File[]>('milestoneDto', 'realisationFiles').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('milestone', 'docs')
        .pipe(
          map(docs =>
            docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      onFileRepresentationChange: (file: FileRepresentation) => {
        store.setNested(
          'milestone',
          'docs',
          store.getNested<ResponseDoc[]>('milestone', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'milestoneDto',
          'docIds',
          store.getNested<number[]>('milestoneDto', 'docIds').filter(id => id !== file.id)
        );
      },
      openFile: (file: FileRepresentation) => openFile?.(file),
      onFileDownLoad: (file: FileRepresentation) => download?.(file),
      onAllRemoved: () => {
        store.setNested('milestone', 'docs', []);
        store.setNested('milestoneDto', 'docIds', []);
      },
    },
  };

  const slotsField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.slots',
    variant: FieldVariant.CUSTOM,
    props: {
      component: PaaSlotsFormComponent,
      errors: store.getObservable('slotErrors'),
      value: store.getNestedObservable<SlotDto[]>('milestoneDto.lots'),
      disabled: isDisabled,
      onChange: (value: SlotDto[]) => {
        const lots = store.getNested<SlotDto[]>('milestoneDto.lots') ?? [];

        lots.forEach((_, index) => {
          store.removeSlotError(index, 'budget');
        });

        const lotsClone = lots.map(lot => ({ ...lot }));

        const updatedLots = value.map((slot, index) => {
          const original = lotsClone[index];

          if (original) {
            if (original.description !== slot.description) {
              store.removeSlotError(index, 'description');
            }
          }

          return original?.id
            ? {
                id: original.id,
                description: slot.description,
                budget: slot.budget || undefined,
              }
            : {
                description: slot.description,
                budget: slot.budget || undefined,
              };
        });

        store.setNested('milestoneDto.lots', updatedLots);
      },
      injectKeys: ['value', 'onChange', 'disabled', 'errors'],
    },
  };

  const fundingSourcesField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.funding',
    variant: FieldVariant.CUSTOM,
    isDisabled,
    props: {
      component: PaaFundingSourceFormComponent,
      errors: store.getObservable('fundingErrors'),
      currencyOptions: currencyOptions,
      financialSourcesOptions: financialSourcesOptions,
      disabled: isDisabled,
      value: store.getNestedObservable<FundingDto[]>('milestoneDto.financements'),
      onChange: (value: FundingDto[]) => {
        const lots = store.getNested<SlotDto[]>('milestoneDto.lots') ?? [];

        lots.forEach((_, index) => {
          store.removeSlotError(index, 'budget');
        });

        const fundings = store.getNested<FundingDto[]>('milestoneDto.financements') ?? [];

        const fundingClone = fundings.map(fund => ({ ...fund }));

        const updatedFundings = value.map((fund, index) => {
          const original = fundingClone[index];

          if (original) {
            if (original.monnaieId !== fund.monnaieId) {
              store.removeFundingError(index, 'monnaieId');
            }
            if (original.sourceFinancement.id !== fund.sourceFinancement.id) {
              store.removeFundingError(index, 'sourceFinancement');
            }
            if (original.budgetEstimer !== fund.budgetEstimer) {
              store.removeFundingError(index, 'budgetEstimer');
            }
          }

          return original?.id
            ? {
                id: original.id,
                budgetEstimer: fund.budgetEstimer,
                sourceFinancement: fund.sourceFinancement,
                monnaieId: fund.monnaieId,
              }
            : {
                budgetEstimer: fund.budgetEstimer,
                sourceFinancement: fund.sourceFinancement,
                monnaieId: fund.monnaieId,
              };
        });

        store.setNested('milestoneDto.financements', updatedFundings);
      },
      injectKeys: ['value', 'onChange', 'disabled', 'errors', 'currencyOptions', 'financialSourcesOptions'],
    },
  };

  return {
    title: 'Milestone Form',
    description: 'Milestone Form Description',
    isHeaderHidden: true,
    class: 'px-4',
    orientation: 'horizontal',
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [labelField, beneficiaryField],
          },
          {
            fields: [typeOfMarketField, selectionModeField],
          },
          {
            fields: [datePresumedLaunchField, datePresumedAchievementField],
          },
          {
            fields: [budgetAllocationField],
          },
          {
            fields: [fundingSourcesField],
          },
          {
            fields: [getFieldBuilderObjectFactory(), budgetEstimatedField],
          },
          {
            fields: [slotsField],
          },
          {
            fields: [uploadField],
          },
        ],
      },
    ],
  };
};
